import { api_configuration } from "../../common";
import DestructorTree from "../DestructorTree";
import { forReference } from "../state/addQueryIfNotExistsAndGet";
import * as sort from "../state/sorting";
import { ensure, isDefined } from "../validation";
import ImplementationVisitorContext from "./ImplementationVisitorContext";
import { initializedReport } from "./instructions/reports";
import api_widget = api_configuration.libraries.gui_definition_lib.types.widget.alan_api;
import csr = api_configuration.types.client_state.alan_api;
import client_state_binder = require("./client_state_binder");
import ClientState = require("../state/ClientState");
import * as addClientState from "../state/addClientState";
import {
	registerModelBindingPropertyPath,
	registerCommand,
	registerAction,
	registerFile,
	registerGroup,
	registerNumber,
	registerStategroup,
	registerText,
	registerJoin,
} from "../state/registerClientState";
import { initializeQueryWithModelBinding } from "./bindQuery";

export function walk_context_ancestor_path(
	path: api_widget.Ccontext_ancestor_path,
	implementation_context: ImplementationVisitorContext
): ImplementationVisitorContext {
	switch (path.properties.has_step.state.name) {
		case "no": {
			const no_has_steps = path.properties.has_step.state.node;
			switch (no_has_steps.properties.context.state.name) {
				case "anonymous": {
					return implementation_context.explicit_context;
				}
				case "implicit": {
					return implementation_context;
				}
				case "named": {
					return implementation_context.explicit_context;
				}
				default:
					return unexpectedState(no_has_steps.properties.context.state);
			}
		}
		case "yes": {
			const yes_has_steps = path.properties.has_step.state.node;
			const parent_implementation_context =
				implementation_context.explicit_context.parent_widget_implementation_context;
			return walk_context_ancestor_path(yes_has_steps.properties.tail, parent_implementation_context);
		}
		case "root":
			return implementation_context.root_widget_implementation_context;
		default:
			return unexpectedState(path.properties.has_step.state);
	}
}

function switchModelBinding(
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree
): ImplementationVisitorContext {
	switch (implementation_context.model_binding.properties.type.state.name) {
		case "collection": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const node = ensure(implementation_context.current_binding_context, csr.Cnode);
			const client_state = registerModelBindingPropertyPath($.properties.property_path, node, destructor);
			const collection_state = addClientState.addCollectionAttribute(
				$.properties.collection.ref.inferences.collection(),
				client_state
			);
			sort.patchCollection(collection_state);
			let new_binding_context: any = collection_state;
			switch ($.properties.join.state.name) {
				case "no":
					break;
				case "yes": {
					new_binding_context = registerJoin(collection_state, destructor);
					break;
				}
				default:
					unexpectedState($.properties.join.state);
			}
			return implementation_context.changeState({
				new_binding_context: new_binding_context,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "ordered graph": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const client_state = ensure(implementation_context.current_binding_context, csr.Cyes__has_ordered_graphs);
			const new_client_state = client_state.properties.ordered_graphs.get($.properties.ordered_graph.entry)!;
			return implementation_context.changeState({
				new_binding_context: new_client_state,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "sort property": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			return implementation_context.changeState({ new_widget_configuration: $.properties.configuration });
		}
		case "command": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const node = ensure(implementation_context.current_binding_context, csr.Cnode);
			const client_state = registerModelBindingPropertyPath($.properties.property_path, node, destructor);
			const operation_attribute = registerCommand(
				$.properties.command.ref.inferences.global_type(),
				client_state,
				destructor
			);
			return implementation_context.changeState({
				new_binding_context: operation_attribute,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "action": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const node = ensure(implementation_context.current_binding_context, csr.Cnode);
			const client_state = registerModelBindingPropertyPath($.properties.property_path, node, destructor);
			const action_attribute = registerAction(
				$.properties.action.ref.inferences.action_type(),
				client_state,
				destructor
			);
			return implementation_context.changeState({
				new_binding_context: action_attribute,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "current node": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const client_state = implementation_context.current_binding_context;
			let node: csr.Cnode;
			if (client_state instanceof csr.Cnode) {
				node = client_state;
			} else if (client_state instanceof csr.Copen_views) {
				node = client_state.properties.view_context.output.result_node();
			} else {
				throw new Error("Expected current binding context to be of type Cnode.");
			}
			node = registerModelBindingPropertyPath($.properties.property_path, node, destructor);
			return implementation_context.changeState({
				new_binding_context: node,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "user": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const user_found = ensure(implementation_context.current_binding_context, csr.Cyes__user_found);
			const user_node = (user_found as any).node;
			return implementation_context.changeState({
				new_binding_context: user_node,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "entity": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			return implementation_context.switchGuiWidgetConfiguration($.properties.configuration);
		}
		case "file": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const node = ensure(implementation_context.current_binding_context, csr.Cnode);
			const client_state = registerModelBindingPropertyPath($.properties.property_path, node, destructor);
			const new_client_state = registerFile($.properties.property.ref.inferences.file(), client_state, destructor);
			return implementation_context.changeState({
				new_binding_context: new_client_state,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "window": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			return implementation_context.changeState({
				new_binding_context: ClientState.client_state.properties.windows.get($.properties.window.entry)!,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "group": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const client_state = ensure(implementation_context.current_binding_context, csr.Cnode);
			const new_client_state = registerGroup($.properties.group.ref.inferences.group(), client_state, destructor);
			return implementation_context.changeState({
				new_binding_context: new_client_state,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "number": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const node = ensure(implementation_context.current_binding_context, csr.Cnode);
			const client_state = registerModelBindingPropertyPath($.properties.property_path, node, destructor);
			const new_client_state = registerNumber($.properties.property.ref.inferences.number(), client_state, destructor);
			return implementation_context.changeState({
				new_binding_context: new_client_state,
				new_widget_configuration: $.properties.configuration,
				// new_number_converter: makeNumberConverter(new_client_state.properties.numerical_type_properties)
			});
		}
		case "query": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			let new_client_state: api_configuration.types.client_state.alan_api.Cquery__client_state;
			const query_context_binding = $.properties.query_context;
			const query_definition = $.properties.query_context.state.node.output.query();
			switch (query_context_binding.state.name) {
				case "node": {
					let client_state = ensure(implementation_context.current_binding_context, csr.Cnode);
					const node_context = query_context_binding.state.node;
					switch (node_context.properties.switch_.state.name) {
						case "root":
							break;
						case "current node":
							client_state = registerModelBindingPropertyPath(
								node_context.properties.switch_.state.node.properties.property_path,
								client_state,
								destructor
							);
							break;
						default:
							unexpectedState(node_context.properties.switch_.state);
					}
					new_client_state = initializeQueryWithModelBinding(client_state, node_context, destructor);
					break;
				}
				case "candidates": {
					const navigable_text_property = ensure(implementation_context.current_binding_context, csr.Cyes__navigable);
					(query_context_binding as any).__configuration = $.properties.configuration; //TODO us an explicit interface or function so that it is clear why this is done.
					new_client_state = forReference(navigable_text_property, query_definition, undefined);
					break;
				}
				default:
					return unexpectedState(query_context_binding.state);
			}

			return implementation_context.changeState({
				new_binding_context: new_client_state,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "query file": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const client_state = ensure(implementation_context.current_binding_context, csr.Centries__yes__has_result);
			const new_client_state = client_state.properties.cells.get($.properties.file.entry)!.properties.type.cast("file");
			return implementation_context.changeState({
				new_binding_context: new_client_state,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "query number": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const column_name = $.properties.number.entry;
			const number_binding = $.inferences.query_number();

			switch (number_binding.properties.type.state.name) {
				case "column": {
					const client_state = ensure(implementation_context.current_binding_context, csr.Cquery__client_state);
					const new_client_state = client_state.properties.columns.get(column_name)!.properties.type.cast("number");
					return implementation_context.changeState({
						new_binding_context: new_client_state,
						new_widget_configuration: $.properties.configuration,
					});
				}
				case "entry": {
					const client_state = ensure(implementation_context.current_binding_context, csr.Centries__yes__has_result);
					const new_client_state = client_state.properties.cells.get(column_name)!.properties.type.cast("number");
					return implementation_context.changeState({
						new_binding_context: new_client_state,
						new_widget_configuration: $.properties.configuration,
					});
				}
				default:
					return unexpectedState(number_binding.properties.type.state);
			}
		}
		case "query text": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const column_name = $.properties.text.entry;
			return $.inferences.query_text().properties.type.switch({
				column: () => {
					const client_state = ensure(implementation_context.current_binding_context, csr.Cquery__client_state);
					const new_client_state = client_state.properties.columns.get(column_name)!.properties.type.cast("text");
					return implementation_context.changeState({
						new_binding_context: new_client_state,
						new_widget_configuration: $.properties.configuration,
					});
				},
				entry: () => {
					const client_state = ensure(implementation_context.current_binding_context, csr.Centries__yes__has_result);
					const new_client_state = client_state.properties.cells.get(column_name)!.properties.type.cast("text");
					return implementation_context.changeState({
						new_binding_context: new_client_state,
						new_widget_configuration: $.properties.configuration,
					});
				},
			});
		}
		case "query stategroup": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const column_name = $.properties.stategroup.entry;
			const stategroup_binding = $.inferences.query_stategroup();
			switch (stategroup_binding.properties.type.state.name) {
				case "column": {
					const client_state = ensure(implementation_context.current_binding_context, csr.Cquery__client_state);
					const new_client_state = client_state.properties.columns
						.get(column_name)!
						.properties.type.cast("state group");
					return implementation_context.changeState({
						new_binding_context: new_client_state,
						new_widget_configuration: $.properties.configuration,
					});
				}
				case "entry": {
					const client_state = ensure(implementation_context.current_binding_context, csr.Centries__yes__has_result);
					const new_client_state = client_state.properties.cells.get(column_name)!.properties.type.cast("state group");
					return implementation_context.changeState({
						new_binding_context: new_client_state,
						new_widget_configuration: $.properties.configuration,
					});
				}
			}
			break;
		}
		case "state group": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const node = ensure(implementation_context.current_binding_context, csr.Cnode);
			const client_state = registerModelBindingPropertyPath($.properties.property_path, node, destructor);
			const new_client_state = registerStategroup(
				$.properties.state_group.ref.inferences.state_group(),
				client_state,
				destructor
			);
			return implementation_context.changeState({
				new_binding_context: new_client_state,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "state": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const client_state = ensure(implementation_context.current_binding_context, csr.Cstate_group__type__property);
			const new_client_state = client_state.properties.states.get(
				$.properties.state_group_context.switch({
					"states list": ($) => $.inferences.state_context().key.ref.key.ref.key,
					"state group binding": ($) => $.properties.state.ref.key.ref.key,
				})
			)!;
			return implementation_context.changeState({
				new_binding_context: new_client_state,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "state rule": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const client_state = ensure(
				implementation_context.current_binding_context,
				csr.Cstates__state_group__type__property
			);
			const new_client_state = client_state.properties.rules.properties.rules.get($.properties.rule.entry)!;
			return implementation_context.changeState({
				new_binding_context: new_client_state,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "text": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const node = ensure(implementation_context.current_binding_context, csr.Cnode);
			const client_state = registerModelBindingPropertyPath($.properties.property_path, node, destructor);
			const new_client_state = registerText($.properties.property.ref.inferences.text(), client_state, destructor);
			return implementation_context.changeState({
				new_binding_context: new_client_state,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "report": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const client_state = ensure(implementation_context.current_binding_context, csr.Cnode);
			const report_type = $.properties.report.entry;
			const new_client_state = client_state.properties.report_types.switchOnEntryExists(
				report_type,
				($) => $,
				() =>
					client_state.properties.report_types.create(report_type, {
						initialized: "no",
					})
			);
			return implementation_context.changeState({
				new_binding_context: new_client_state,
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "report text parameter": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const client_state = initializedReport(
				ensure(implementation_context.current_binding_context, csr.Creport_types__node)
			).properties.parameters;
			return implementation_context.changeState({
				new_binding_context: client_state.properties.parameters
					.get($.properties.text.entry)!
					.properties.type.cast("text"),
				new_widget_configuration: $.properties.configuration,
			});
		}
		case "report number parameter": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const client_state = initializedReport(
				ensure(implementation_context.current_binding_context, csr.Creport_types__node)
			).properties.parameters;
			const report_number = client_state.properties.parameters
				.get($.properties.number.entry)!
				.properties.type.cast("number");
			return implementation_context.changeState({
				new_binding_context: report_number,
				new_widget_configuration: $.properties.configuration,
				// new_number_converter: makeNumberConverter(report_number.properties.numerical_type_properties)
			});
		}
		case "joined entry": {
			const $ = implementation_context.model_binding.properties.type.state.node;
			const client_state = ensure(implementation_context.current_binding_context, csr.Centry__referenced_entry);
			return implementation_context.changeState({
				new_binding_context: client_state.properties.entry.ref,
				new_widget_configuration: $.properties.configuration,
			});
		}
		default:
			unexpectedState(implementation_context.model_binding.properties.type.state);
	}
}

export function context_selection_path(
	path: api_widget.Ccontext_selection_path,
	implementationVisitorContext: ImplementationVisitorContext,
	destructor: DestructorTree
): ImplementationVisitorContext {
	switch (path.properties.has_steps.state.name) {
		case "no": {
			return implementationVisitorContext;
		}
		case "yes": {
			const $ = path.properties.has_steps.state.node;
			const new_implementation_context = context_selection_path_step_type(
				$.properties.type,
				implementationVisitorContext,
				destructor
			);
			return context_selection_path($.properties.tail, new_implementation_context, destructor);
		}
		default:
			unexpectedState(path.properties.has_steps.state);
	}
}

function context_selection_path_step_type(
	step_type: api_widget.Cyes__has_steps__context_selection_path["properties"]["type"],
	implementationVisitorContext: ImplementationVisitorContext,
	destructor: DestructorTree
): ImplementationVisitorContext {
	switch (step_type.state.name) {
		case "client binding": {
			const $ = step_type.state.node;
			const binding_name = $.properties.binding.entry;
			const binding_type_name = $.properties.binding.ref.properties.binding.entry;
			return isDefined(implementationVisitorContext.client_state_binder_AsPropertiesBinding.bindings)
				[binding_name](implementationVisitorContext, destructor)
				.switchClientStateBinder((client_state_binder as any).binding[binding_type_name]);
		}
		case "constrained configuration attribute": {
			const $ = step_type.state.node;
			const binding_type_name = $.properties.configuration_attribute.inferences
				.binding()
				.properties.constrained_on_containing_binding.state.node.output.client_binding().key;
			const configuration_attribute_name = $.properties.configuration_attribute.entry;
			const new_model_binding = implementationVisitorContext.gui_widget_configuration.properties.configuration
				.get(configuration_attribute_name)!
				.properties.attribute.properties.type.cast("model binding");
			return switchModelBinding(
				implementationVisitorContext
					.switchGuiModelBinding(new_model_binding)
					.switchClientStateBinder((client_state_binder as any).binding[binding_type_name]),
				destructor
			);
		}
		case "unconstrained configuration attribute": {
			const $ = step_type.state.node;
			const binding_name = $.properties.configuration_attribute.inferences
				.binding()
				.properties.constrained_on_containing_binding.state.node.output.client_binding().key;
			const configuration_attribute_name = $.properties.configuration_attribute.entry;
			const new_gui_model_binding = implementationVisitorContext.gui_widget_configuration.properties.configuration
				.get(configuration_attribute_name)!
				.properties.attribute.properties.type.cast("model binding");
			return switchModelBinding(
				implementationVisitorContext
					.switchGuiModelBinding(new_gui_model_binding)
					.switchClientStateBinder((client_state_binder as any).binding[binding_name]),
				destructor
			);
		}
		default:
			unexpectedState(step_type.state);
	}
}

export function context_selection(
	context_selection: api_widget.Ccontext_selection,
	implementationVisitorContext: ImplementationVisitorContext,
	destructor: DestructorTree
): ImplementationVisitorContext {
	switch (context_selection.properties.change_context_to.state.name) {
		case "other context": {
			const $ = context_selection.properties.change_context_to.state.node;
			const parent_implementation_context = walk_context_ancestor_path(
				$.properties.ancestor_path,
				implementationVisitorContext
			);
			let new_implementation_context = implementationVisitorContext
				.changeState({
					new_widget_configuration: parent_implementation_context.widget_configuration,
					new_model_binding: parent_implementation_context.raw_model_binding,
					entry_key: parent_implementation_context.entry_keys,
					id_generator: parent_implementation_context.id_generator,
					new_last_gui_widget_configuration: parent_implementation_context.last_gui_widget_configuration,
				})
				.changeState({
					new_binding_context: parent_implementation_context.current_binding_context,
				});
			if (parent_implementation_context.client_state_binder !== null) {
				new_implementation_context = new_implementation_context.changeState({
					new_client_state_binder: parent_implementation_context.client_state_binder,
				});
			}
			return context_selection_path($.properties.path, new_implementation_context, destructor);
		}
		case "engine state": {
			const $ = context_selection.properties.change_context_to.state.node;
			return implementationVisitorContext
				.switchBindingContext(ClientState.client_state)
				.switchClientStateBinder((client_state_binder as any).binding[$.properties.engine_state_binding.entry]);
		}
		default:
			unexpectedState(context_selection.properties.change_context_to.state);
	}
}
